import { Avatar, Button, Grid, makeStyles, Theme } from "@material-ui/core";
import React, { useState } from "react";
import { RecordHeader } from "@ofqual/portal-components/components/RecordHeader";
import { NonNullableItemState } from "../../redux/state";
import { InformationRequestStatus } from "../../domain/InformationRequest";
import {
  formatUtcDate,
  standardDateFormats,
} from "@ofqual/portal-core/core/utils/dateUtils";
import { RecipientOrg } from "../../domain/RecipientsViewModel";
import { useAuth } from "@ofqual/portal-core/core/hooks/useAuth";
import { UserType } from "../../redux/types";
import { AssignDialog } from "./AssignDialog";
import { AssignOfqualUserDialog } from "./AssignOfqualUserDialog";
import { authSelector } from "../../redux/selectors";

const useStyles = makeStyles((theme: Theme) => {
  return {
    gridItem: {
      borderRight: "1px solid rgba(255,255,255,0.2)",
      "& div": {
        paddingBottom: "5px",
      },
    },
    gridButton: {
      color: "#f5f5f5",
    },
    avatar: {
      backgroundColor: "#293445",
      width: 70,
      height: 70,
      fontSize: 30,
      textTransform: "uppercase",
    },
  };
});

type ItemHeaderProps = {
  state: NonNullableItemState | null;
  recipientOrg: RecipientOrg | null;
};

export const ItemHeader: React.FC<ItemHeaderProps> = ({
  state,
  recipientOrg,
}) => {
  const classes = useStyles();
  const { userType } = useAuth(authSelector);
  const [isDialogOpen, setIsOpenDialog] = useState(false);
  const [isOfqualAssignDialogOpen, setOfqualAssignDialog] = useState(false);

  const calculateInitials = (name: string): string => {
    return name
      .split(" ")
      .map((word) => word[0])
      .join("");
  };

  console.log("ao", state);
  return (
    <>
      <RecordHeader>
        {state?.item && recipientOrg ? (
          <Grid
            container
            direction="row"
            justify="flex-start"
            alignItems="stretch"
            spacing={2}
          >
            <Grid className={classes.gridItem} item>
              <div>
                <label>
                  <strong>Created By: </strong>
                </label>
                <span data-test="metadata-created-by">
                  {state.item.createdByDisplayName}
                </span>
              </div>
              <div>
                <label>
                  <strong>Created At: </strong>
                </label>
                <span data-test="metadata-created-date">
                  {formatUtcDate(
                    state?.item.createdDate,
                    standardDateFormats.DATE_TIME
                  )}
                </span>
              </div>
              <div>
                <label>
                  <strong>Status: </strong>
                </label>
                <span data-test="metadata-status">
                  {recipientOrg?.status === "Unread" &&
                  state.item.status === InformationRequestStatus.Draft
                    ? "Draft"
                    : recipientOrg?.status}
                </span>
              </div>
            </Grid>

            {state.item.modifiedByDisplayName &&
            state.item.modifiedDate &&
            userType !== UserType.AO ? (
              <Grid className={classes.gridItem} item>
                <div>
                  <label>
                    <strong>Updated By: </strong>
                  </label>
                  <span data-test="metadata-updated-by">
                    {state.item.modifiedByDisplayName}
                  </span>
                </div>
                <div>
                  <label>
                    <strong>Updated At: </strong>
                  </label>
                  <span data-test="metadata-updated-date">
                    {formatUtcDate(
                      state.item.modifiedDate,
                      standardDateFormats.DATE_TIME
                    )}
                  </span>
                </div>
              </Grid>
            ) : null}
            {userType === UserType.Ofqual ? (
              <Grid className={classes.gridItem} item>
                <Button
                  onClick={() => setOfqualAssignDialog(true)}
                  data-test="assigned-to"
                  className={classes.gridButton}
                >
                  <Avatar className={classes.avatar}>
                    {recipientOrg.assignedToDisplayName
                      ? calculateInitials(recipientOrg.assignedToDisplayName)
                      : ""}
                  </Avatar>
                  {recipientOrg.assignedToDisplayName ? (
                    <div>
                      <label>
                        <strong>Assigned to:</strong>
                      </label>
                      <br></br>
                      <span data-test="metadata-assigned-to">
                        {recipientOrg?.assignedToDisplayName || "Unassigned"}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <label>
                        <strong>Assign to</strong>
                      </label>
                    </div>
                  )}
                </Button>
              </Grid>
            ) : (
              <Grid
                className={classes.gridItem}
                item
                style={{ display: "inherit" }}
              >
                <Avatar className={classes.avatar}>
                  {recipientOrg.assignedToDisplayName
                    ? calculateInitials(recipientOrg.assignedToDisplayName)
                    : ""}
                </Avatar>
                <div style={{ padding: 15, textTransform: "uppercase" }}>
                  <label>
                    <strong>Assigned to (Ofqual):</strong>
                  </label>
                  <br></br>
                  <span data-test="metadata-assigned-to">
                    {recipientOrg?.assignedToDisplayName || "Unassigned"}
                  </span>
                </div>
              </Grid>
            )}
            {userType === UserType.AO &&
            recipientOrg &&
            !recipientOrg.closed &&
            state.item.allowAoReassignment ? (
              <Grid className={classes.gridItem} item>
                <Button
                  onClick={() => setIsOpenDialog(true)}
                  data-test="org-assigned-to"
                  className={classes.gridButton}
                >
                  <Avatar className={classes.avatar}>
                    {recipientOrg.assignedToDisplayNameAO
                      ? calculateInitials(recipientOrg.assignedToDisplayNameAO)
                      : ""}
                  </Avatar>
                  {recipientOrg.assignedToDisplayNameAO ? (
                    <div>
                      <label>
                        <strong>Assigned to:</strong>
                      </label>
                      <br></br>
                      <span data-test="metadata-assigned-to">
                        {recipientOrg?.assignedToDisplayNameAO || "Unassigned"}
                      </span>
                    </div>
                  ) : (
                    <div>
                      <label>
                        <strong>Assign to</strong>
                      </label>
                    </div>
                  )}
                </Button>
              </Grid>
            ) : (
              <Grid
                className={classes.gridItem}
                item
                style={{ display: "inherit" }}
              >
                <Avatar className={classes.avatar}>
                  {recipientOrg.assignedToDisplayNameAO
                    ? calculateInitials(recipientOrg.assignedToDisplayNameAO)
                    : ""}
                </Avatar>
                <div style={{ padding: 15, textTransform: "uppercase" }}>
                  <label>
                    <strong>Assigned to:</strong>
                  </label>
                  <br></br>
                  <span data-test="metadata-assigned-to">
                    {recipientOrg.assignedToDisplayNameAO || "Unassigned"}
                  </span>
                </div>
              </Grid>
            )}
          </Grid>
        ) : null}
      </RecordHeader>
      <AssignDialog
        recipientOrg={recipientOrg!}
        informationRequestId={state?.item.id!}
        onClose={() => setIsOpenDialog(false)}
        open={isDialogOpen}
      />
      <AssignOfqualUserDialog
        recipientOrg={recipientOrg!}
        informationRequestId={state?.item.id!}
        onClose={() => setOfqualAssignDialog(false)}
        open={isOfqualAssignDialogOpen}
      />
    </>
  );
};
